import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject } from 'rxjs';
import { SnackbarService } from 'ok-angular-shared/components';
import { EllipsisTooltipDirective } from 'ok-angular-shared/directives';
import { ChargingUser, PreviousAccessGroup, PreviousAccessGroupsSearchEvent } from '../charging-user.model';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { DiscountType } from 'src/app/shared/enums/discount-type.enum';
import { AccessGroupUserType } from 'src/app/shared/enums/access-group-user-type';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { PagingOptions } from 'src/app/shared/models/paging';
import { LoadingSpinnerComponent } from 'src/app/core/loading-spinner/loading-spinner.component';

export interface IChargingUserPreviousAccessGroupsModalData {
  chargingUser: ChargingUser;
}

@Component({
  selector: 'adm-charging-user-previous-accessgroups-modal',
  templateUrl: './charging-user-previous-accessgroups-modal.component.html',
  styleUrl: './charging-user-previous-accessgroups-modal.component.scss',
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    DkCurrencyPipe,
    LoadingSpinnerComponent,
    EllipsisTooltipDirective,
  ],
})
export class ChargingUserPreviousAccessGroupsModalComponent extends PagerBase implements OnInit {
  dialogRef = inject<MatDialogRef<ChargingUserPreviousAccessGroupsModalComponent>>(MatDialogRef);
  data = inject<IChargingUserPreviousAccessGroupsModalData>(MAT_DIALOG_DATA);
  private chargingUserService = inject(ChargingUserService);
  private snackbarService = inject(SnackbarService);

  public chargingUser: ChargingUser = this.data?.chargingUser;
  public discountType = DiscountType;
  public accessGroupUserType = AccessGroupUserType;
  public displayColumns = ['name', 'from', 'to', 'timeLimited', 'discount'];
  public dataSource = new MatTableDataSource<PreviousAccessGroup>();
  public totalCount = 0;
  public currentPage = 0;
  public pagingOptions: PagingOptions;
  public loading$ = new BehaviorSubject<boolean>(false);

  private previousAccessGroupsSearchEvent: PreviousAccessGroupsSearchEvent;

  ngOnInit(): void {
    const pageEvent = new PageEvent();
    pageEvent.pageIndex = 0;
    pageEvent.pageSize = this.defaultModalPageSize;

    this.previousAccessGroupsSearchEvent = new PreviousAccessGroupsSearchEvent(pageEvent);
    this.previousAccessGroupsSearchEvent.chargingUserId = this.chargingUser.id;

    this.pagingOptions = {
      pageSize: pageEvent.pageSize,
      pageSizeOptions: this.modalPageSizes,
    };

    this.loadPreviousAccessGroups();
  }

  public onPageChanged(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.previousAccessGroupsSearchEvent.paging.pageIndex = event.pageIndex;
    this.previousAccessGroupsSearchEvent.paging.pageSize = event.pageSize;
    this.loadPreviousAccessGroups();
  }

  public close(): void {
    this.dialogRef.close();
  }

  public getTo(accessGroup: PreviousAccessGroup): Date | null | undefined {
    return accessGroup.activeTo ?? accessGroup.deleted;
  }

  private loadPreviousAccessGroups(): void {
    this.loading$.next(true);
    this.chargingUserService.getPreviousAccessGroups(this.previousAccessGroupsSearchEvent).subscribe({
      next: result => {
        this.dataSource.data = result.items;
        this.totalCount = result.totalCount;
        this.currentPage = result.pageIndex;
        this.loading$.next(false);
      },
      error: () => {
        this.snackbarService.error('Der skete en fejl under hentning af tidligere adgangsgrupper.');
        this.loading$.next(false);
      },
    });
  }
}
